import Axios, {
  AxiosError,
  AxiosRequestTransformer,
  AxiosResponse,
  AxiosResponseTransformer,
} from "axios"
import { camelCase, snakeCase } from "lodash"

import { getAuthInfo } from "@/lib/auth"

import {
  APIError,
  paramsSerializer,
  refreshToken,
  transformKeys,
} from "./utils"

/**
 * __noTransform is used to bypass snakeCase conversion. Reason for doing this is:
 * newPassword1 gets converted to new_password_1
 * But, the backend expects new_password1
 * However, the proper snake case implementation is new_password_1
 *
 * This work-around will be removed when backend has fixed their APIs
 */
const transformRequest: AxiosRequestTransformer = (data) => {
  if (data?.__noTransform) {
    delete data["__noTransform"]
    return data
  }

  return transformKeys(data, snakeCase)
}

const transformResponse: AxiosResponseTransformer = (data) => {
  return transformKeys(data, camelCase)
}

const axios = Axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  withCredentials: true,
  paramsSerializer,
  transformRequest: [
    transformRequest,
    ...(Array.isArray(Axios.defaults.transformRequest)
      ? Axios.defaults.transformRequest
      : []),
  ],
  transformResponse: [
    ...(Array.isArray(Axios.defaults.transformResponse)
      ? Axios.defaults.transformResponse
      : []),
    transformResponse,
  ],
  headers: {
    "Content-Type": "application/json",
  },
})

axios.interceptors.request.use(async (request) => {
  const token = getAuthInfo()?.accessToken

  if (token) {
    const tokenString = `Bearer ${token}`
    request.headers["Authorization"] = tokenString
  }

  return request
})

const successInterceptor = async (response: AxiosResponse<any>) => {
  if (response.config.raw) return response.data
  return response.data.data
}

const errorInterceptor = async (error: AxiosError) => {
  if (error.response?.status === 401) {
    const refreshed = await refreshToken()
    if (refreshed && error.config) {
      error.config.headers["Authorization"] =
        `Bearer ${getAuthInfo()?.accessToken}`
      return axios.request(error.config)
    }
  }

  throw new APIError(error)
}

axios.interceptors.response.use(successInterceptor, errorInterceptor)

export default axios
